import UnauthenticatedError from '@/api/helpers/UnauthenticatedError';
import SecurityRepository from '@/api/repositories/SecurityRepository';
import {addTime, isAfter} from '@/shared/dateTimeHelper';
import {useMsTeamsStore} from '@/stores/msTeamsStore';
import {useOidcStore} from '@/stores/oidcStore';
import {useSecurityStore} from '@/stores/securityStore';
import {until} from '@vueuse/core';
import {storeToRefs} from 'pinia';

export const refreshBeforeExpiryMs = 5 * 60 * 1_000;
const minRefreshThresholdMs = 10 * 1_000;
const relativeRefreshThreshold = 0.05;

export async function handleTokenRefresh(): Promise<void> {
    const {tokenExpiry, tokenExpiryDuration, isRefreshingToken, lastRefreshAttempt} =
        storeToRefs(useSecurityStore());
    if (isRefreshingToken.value) {
        await until(isRefreshingToken).not.toBeTruthy({timeout: 30_000});
        return;
    }

    if (!tokenExpiry.value || isWaitingSinceLastRefresh(lastRefreshAttempt.value)) {
        return;
    }

    // Only refresh if token expires within the effective threshold
    if (!isAfterThreshold(tokenExpiry.value, tokenExpiryDuration.value)) {
        return;
    }

    isRefreshingToken.value = true;
    try {
        switch (getEffectiveAuthType()) {
            case 'msal':
                await refreshMsalToken();
                break;
            case 'refresh':
                await refreshWithRefreshToken();
                break;
            case 'oidc':
                await refreshOidc();
                break;
        }
    } catch {
        // Swallow errors here
        // lastRefreshAttempt logic will prevent "inifinite" re-tries
    } finally {
        isRefreshingToken.value = false;
        lastRefreshAttempt.value = new Date();
    }
}

/**
 * Calculate whether the token expiry is within the effective threshold.
 * This is to prevent clock skew cases where the token is expired but the client thinks it's still valid.
 */
function isAfterThreshold(tokenExpiry: number, tokenExpiryDuration: number | null) {
    const now = Date.now();
    const timeUntilExpiry = tokenExpiry - now;

    // Calculate hybrid threshold: use absolute or relative threshold, whichever is larger
    // This ensures we refresh earlier for longer-lived tokens
    const relativeThreshold = tokenExpiryDuration
        ? tokenExpiryDuration * relativeRefreshThreshold
        : 0;
    const effectiveThreshold = Math.max(
        minRefreshThresholdMs,
        refreshBeforeExpiryMs,
        relativeThreshold
    );
    return timeUntilExpiry < effectiveThreshold;
}

function isWaitingSinceLastRefresh(lastRefreshAttempt: Date | null) {
    return lastRefreshAttempt && isAfter(addTime(lastRefreshAttempt, 1, 'minute'), new Date());
}

async function refreshOidc() {
    const {accessToken} = storeToRefs(useOidcStore());
    const {default: OIDCRepository} = await import('@/api/repositories/OIDCRepository');
    const result = await OIDCRepository().fetchRefreshToken();
    if ('access_token' in result) {
        accessToken.value = result.access_token;
    }
}

async function refreshWithRefreshToken() {
    const {refreshToken, token} = storeToRefs(useSecurityStore());
    try {
        const value = await SecurityRepository().refresh(refreshToken.value);
        token.value = value.token;
        // Keep the current refresh token if the response did not contain a valid one
        if (value.refreshToken) {
            refreshToken.value = value.refreshToken;
        }
    } catch (error) {
        if (error instanceof UnauthenticatedError) {
            useSecurityStore().clearToken(true);
        }
    }
}

async function refreshMsalToken() {
    const {isMSTeams} = useMsTeamsStore();
    if (isMSTeams) {
        return;
    }
    if (__IS_HYBRID_APP__) {
        const {hybridAppAzureRefresh} = await import('@/api/authentication/hybridAppAzureLogin');
        await hybridAppAzureRefresh();
        return;
    }
    const {initMsalSilentLogin} = await import('@/api/authentication/msal');
    await initMsalSilentLogin();
}

function getEffectiveAuthType() {
    const {authType} = storeToRefs(useSecurityStore());
    const {hasOidcToken} = storeToRefs(useOidcStore());
    if (authType.value === 'MS-Access-Token') {
        return 'msal';
    } else if (hasOidcToken.value) {
        return 'oidc';
    } else {
        return 'refresh';
    }
}
